// Leaderboard.jsx
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import "./BadgeLeaderboard.css";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useDispatch, useSelector } from "react-redux";
import { getLeaderboardDataAction } from "../../../actions/Leaderboard/leaderboardActions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

// Tech companies data with their logos and skill requirements
const TECH_COMPANIES = [
  {
    name: "Google",
    logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg",
    tier: "elite",
    minScore: 95,
    color: "#4285F4",
  },
  {
    name: "Meta",
    logo: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg",
    tier: "elite",
    minScore: 95,
    color: "#0081FB",
  },
  {
    name: "Amazon",
    logo: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg",
    tier: "advanced",
    minScore: 85,
    color: "#FF9900",
  },
  {
    name: "Microsoft",
    logo: "https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg",
    tier: "advanced",
    minScore: 85,
    color: "#00A4EF",
  },
  {
    name: "Netflix",
    logo: "https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg",
    tier: "intermediate",
    minScore: 75,
    color: "#E50914",
  },
  {
    name: "Spotify",
    logo: "https://upload.wikimedia.org/wikipedia/commons/1/19/Spotify_logo_without_text.svg",
    tier: "intermediate",
    minScore: 75,
    color: "#1DB954",
  },
];

const BadgeLeaderboard = ({
  certificateName,
  technologyFieldName,
  certificateImage,
  sub_id,
}) => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const history = useHistory();
  const getCertificateSlug = (subId) => `${subId}-development`;

  // Fetch leaderboard data from Redux store
  const {
    result: leaderboardResult,
    loading: leaderboardLoading,
    error: leaderboardError,
  } = useSelector((state) => state.getLeaderboardDataStore);

  // Dispatch action to fetch leaderboard data on component mount
  useEffect(() => {
    if (userInfo) {
      dispatch(getLeaderboardDataAction());
    }
  }, [userInfo, dispatch]);

  // Extract leaderboard data from Redux result
  const leaderboardData = leaderboardResult?.leaderboard || [];

  // Find current user from leaderboard
  const currentUser = leaderboardData?.find((user) => user?.isUser);

  // Get initials from name
  const getInitials = (name) => {
    const nameParts = name?.trim().split(" ");
    const firstInitial = nameParts?.[0] ? nameParts[0][0].toUpperCase() : "";
    const lastInitial = nameParts?.[1] ? nameParts[1][0].toUpperCase() : "";
    return firstInitial + lastInitial;
  };

  // Generate avatar colors
  const getAvatarColor = (index, isCurrentUser = false) => {
    if (isCurrentUser) return "#FFD700";
    const colors = [
      "#B8860B",
      "#DAA520",
      "#FFA500",
      "#FF8C00",
      "#CD853F",
      "#C5B358",
    ];
    return colors[index % colors.length];
  };

  // Get rank badge component
  const getRankBadge = (rank) => {
    if (rank === 1) return <span className="leaderboard-medal">🥇</span>;
    if (rank === 2) return <span className="leaderboard-medal">🥈</span>;
    if (rank === 3) return <span className="leaderboard-medal">🥉</span>;
    return <span className="leaderboard-rank-number">#{rank}</span>;
  };

  return (
    <div className="leaderboard-container">
      {/* Header */}
      <div className="leaderboard-header">
        <div className="leaderboard-title-section">
          <EmojiEventsIcon className="leaderboard-title-icon" />
          <h2 className="leaderboard-title">Leaderboard</h2>
        </div>
      </div>

      {/* Current User Highlight Card */}
      <motion.div
        className="leaderboard-user-highlight"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: "spring" }}
      >
        <div className="leaderboard-user-highlight-content">
          <div className="leaderboard-user-highlight-left">
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className="leaderboard-user-highlight-avatar-wrapper">
                <div
                  className="leaderboard-item-avatar"
                  style={{
                    backgroundColor: getAvatarColor(0, true),
                  }}
                >
                  {getInitials("you")}
                </div>
              </div>
              <div className="leaderboard-user-highlight-info">
                <h3 className="leaderboard-user-highlight-name">
                  {currentUser?.fullName}

                  <motion.span
                    className="leaderboard-trend-up"
                    initial={{ y: 5, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                  >
                    <TrendingUpIcon fontSize="small" />2
                  </motion.span>
                </h3>
                <p className="leaderboard-user-highlight-tech">
                  {certificateName}
                </p>
              </div>
            </div>
            <div className="leaderboard-user-highlight-rank">
              <WorkspacePremiumIcon className="leaderboard-rank-icon" />
              <span className="leaderboard-rank-text">
                #{currentUser?.rank}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Career Opportunities Section */}
      <motion.div
        className="leaderboard-companies-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="leaderboard-companies-header">
          <h3 className="leaderboard-companies-title">
            🚀 Career Opportunities
          </h3>
        </div>

        <div className="leaderboard-companies-unlocked">
          <p className="leaderboard-companies-subtitle">
            Your score qualifies you for positions at:
          </p>
          <div className="leaderboard-companies-grid">
            {TECH_COMPANIES.map((company, index) => (
              <motion.div
                key={company.name}
                className="leaderboard-company-card"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div className="leaderboard-company-logo">
                  <img src={company.logo} alt={company.name} />
                </div>
                <span className="leaderboard-company-name">{company.name}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Certificate Section - Replacing Next Milestone */}
        <div className="leaderboard-certificate-section">
          <p className="leaderboard-certificate-title">
            🎯 Let's get you hired
          </p>
          <motion.div
            className="leaderboard-certificate-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="leaderboard-certificate-image">
              <img src={certificateImage} alt="Certificate" />
            </div>
            <div className="leaderboard-certificate-content">
              <h4 className="leaderboard-certificate-name">
                {technologyFieldName}
              </h4>
              <p className="leaderboard-certificate-description">Get it now</p>
              <motion.button
                className="leaderboard-unlock-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  history.push(
                    `/certificates/list/${getCertificateSlug(sub_id)}`
                  )
                }
              >
                Unlock now
                <motion.span
                  className="leaderboard-arrow-icon"
                  animate={{ x: [0, 5, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <ArrowForwardIcon fontSize="small" />
                </motion.span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Leaderboard Rankings */}
      <div className="leaderboard-rankings">
        <h3 className="leaderboard-rankings-title">Top Performers</h3>

        <div className="leaderboard-list">
          {leaderboardData?.map((user, index) => (
            <motion.div
              key={user?.fullName}
              className={`leaderboard-item ${
                user?.isUser ? "current-user-item" : ""
              } ${user?.rank <= 3 ? "top-three" : ""}`}
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.02, x: 5 }}
            >
              <div className="leaderboard-item-left">
                <div className="leaderboard-item-rank">
                  {getRankBadge(user?.rank)}
                </div>
                <div
                  className="leaderboard-item-avatar"
                  style={{
                    backgroundColor: getAvatarColor(index, user.isCurrentUser),
                  }}
                >
                  {user.avatar ? (
                    <img src={user?.avatar} alt={user?.name} />
                  ) : (
                    getInitials(user?.fullName)
                  )}
                </div>
                <div className="leaderboard-item-info">
                  <span className="leaderboard-item-name">
                    {user?.fullName}
                    {user?.isCurrentUser && (
                      <span className="leaderboard-you-badge">You</span>
                    )}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BadgeLeaderboard;
